import { useEffect } from "react";
import { localBackend } from "@/integrations/local/client";

interface Integrations {
  gtm_id?: string;
  ga4_id?: string;
  meta_pixel_id?: string;
  gsc_verification?: string;
  head_snippet?: string;
  body_snippet?: string;
}

const MARK = "data-cloud-integration";
const GTM_ID_PATTERN = /^GTM-[A-Z0-9]+$/;
const GA4_ID_PATTERN = /^G-[A-Z0-9]+$/;
const GOOGLE_TAG_SNIPPET_PATTERN =
  /googletagmanager\.com\/(?:gtm|gtag)\/|GTM-[A-Z0-9]+|AW-\d+/i;

function clear() {
  document.querySelectorAll(`[${MARK}]`).forEach((n) => n.remove());
}

function normalizeGoogleId(value: string | undefined, pattern: RegExp) {
  const id = value?.trim().toUpperCase() ?? "";
  return pattern.test(id) ? id : "";
}

function snippetsContainId(cfg: Integrations, id: string) {
  const needle = id.toLowerCase();
  return [cfg.head_snippet, cfg.body_snippet].some((snippet) =>
    snippet?.toLowerCase().includes(needle),
  );
}

function snippetsContainGoogleTag(cfg: Integrations) {
  return [cfg.head_snippet, cfg.body_snippet].some((snippet) =>
    GOOGLE_TAG_SNIPPET_PATTERN.test(snippet ?? ""),
  );
}

function existingMarkupContainsId(id: string) {
  const needle = id.toLowerCase();
  return Array.from(document.querySelectorAll("script, iframe")).some(
    (node) => {
      const src = "src" in node ? String(node.src).toLowerCase() : "";
      const text = node.textContent?.toLowerCase() ?? "";
      return src.includes(needle) || text.includes(needle);
    },
  );
}

function shouldInjectConfiguredId(cfg: Integrations, id: string) {
  return !snippetsContainId(cfg, id) && !existingMarkupContainsId(id);
}

function appendHTML(target: HTMLElement, html: string, tag: string) {
  if (!html) return;
  const tpl = document.createElement("template");
  tpl.innerHTML = html;
  Array.from(tpl.content.childNodes).forEach((node) => {
    if (node.nodeType === 1) {
      const el = node as HTMLElement;
      // re-create script tags so they execute
      if (el.tagName === "SCRIPT") {
        const s = document.createElement("script");
        for (const a of Array.from(el.attributes))
          s.setAttribute(a.name, a.value);
        s.text = el.textContent ?? "";
        s.setAttribute(MARK, tag);
        target.appendChild(s);
        return;
      }
      if (target === document.head && el.tagName === "NOSCRIPT") {
        el.setAttribute(MARK, tag);
        document.body.insertBefore(el, document.body.firstChild);
        return;
      }
      el.setAttribute(MARK, tag);
      target.appendChild(el);
    } else {
      target.appendChild(node.cloneNode(true));
    }
  });
}

function inject(cfg: Integrations) {
  clear();
  const head = document.head;
  const body = document.body;
  const gtmId = normalizeGoogleId(cfg.gtm_id, GTM_ID_PATTERN);
  const ga4Id = normalizeGoogleId(cfg.ga4_id, GA4_ID_PATTERN);
  const hasGoogleTagSnippet = snippetsContainGoogleTag(cfg);

  if (cfg.gsc_verification) {
    const m = document.createElement("meta");
    m.name = "google-site-verification";
    m.content = cfg.gsc_verification;
    m.setAttribute(MARK, "gsc");
    head.appendChild(m);
  }

  if (gtmId && shouldInjectConfiguredId(cfg, gtmId)) {
    const s = document.createElement("script");
    s.setAttribute(MARK, "gtm");
    s.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`;
    head.appendChild(s);
    const ns = document.createElement("noscript");
    ns.setAttribute(MARK, "gtm");
    ns.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    body.insertBefore(ns, body.firstChild);
  }

  if (
    ga4Id &&
    !gtmId &&
    !hasGoogleTagSnippet &&
    shouldInjectConfiguredId(cfg, ga4Id)
  ) {
    const s1 = document.createElement("script");
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${ga4Id}`;
    s1.setAttribute(MARK, "ga4");
    head.appendChild(s1);
    const s2 = document.createElement("script");
    s2.setAttribute(MARK, "ga4");
    s2.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${ga4Id}');`;
    head.appendChild(s2);
  }

  if (cfg.meta_pixel_id) {
    const s = document.createElement("script");
    s.setAttribute(MARK, "fbpx");
    s.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${cfg.meta_pixel_id}');fbq('track','PageView');`;
    head.appendChild(s);
    const ns = document.createElement("noscript");
    ns.setAttribute(MARK, "fbpx");
    ns.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${cfg.meta_pixel_id}&ev=PageView&noscript=1" />`;
    body.appendChild(ns);
  }

  if (cfg.head_snippet) appendHTML(head, cfg.head_snippet, "head-custom");
  if (cfg.body_snippet) appendHTML(body, cfg.body_snippet, "body-custom");
}

export function SiteIntegrations() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.pathname.startsWith("/admin")) return;
    let cancelled = false;
    (async () => {
      const { data } = await localBackend
        .from("site_settings")
        .select("value")
        .eq("key", "integrations")
        .maybeSingle();
      if (cancelled) return;
      if (data?.value) inject(data.value as Integrations);
    })();
    return () => {
      cancelled = true;
      clear();
    };
  }, []);
  return null;
}
